import { IFlowRepository } from './IFlowRepository';
import { ApiResponseModel, JSONMap } from 'models/ApiResponseModel';
import type BaseApiProvider from 'providers/BaseApiProvider';
import { injectable, inject } from 'inversify';
import Logger from 'utilities/log';
import {PaginationModel} from 'models/PaginationModel';
import FlowModel, { FlowGetPayloadFilterModel, FlowGetPayloadModel, FlowPayloadModel } from 'models/flow/FlowModel';
import FlowSettingModel, { FlowSettingGetPayloadModel, FlowSettingPayloadModel } from 'models/flow/FlowSettingModel';
import FlowSettingResultModel, { FlowSettingResultGetPayloadModel, FlowSettingResultPayloadModel } from 'models/flow/FlowSettingResultModel';
import FlowFormBuilderDesignModel, { FlowFormBuilderDesignGetPayloadModel, FlowFormBuilderDesignPayloadListModel, FlowFormBuilderDesignPayloadModel } from 'models/flow/FlowFormBuilderDesignModel';

@injectable()
class FlowRepository implements IFlowRepository {

    @inject(Symbol.for("BaseApiProvider_5081")) private _provider?: BaseApiProvider;

   //#region Flow 
    
   public async GetAllFlowInfo(params: FlowGetPayloadModel): Promise<{
    rows: FlowModel[];
    pagination: PaginationModel;
    } | null> {
        try {
            const url = `Api/FlowInfo/GetAllFlows`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get Flows list');
                return null;
            }

            return {
                rows: response["data"]["rows"].map((js: any) => FlowModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get Flows list: ${error}`);
            throw new Error(`Failed to get smslogs list: ${error}`);
        }
    }
    public async GetFlowInfo(params: FlowGetPayloadModel): Promise<FlowModel| null> {
        try {
            const url = `Api/FlowInfo/GetFlow`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get base info');
                return null;
            }
            return FlowModel.fromJson(response["data"]);

        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
                throw new Error(`Api failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
                throw new Error('Api failed: Unknown error');
            }
        }
    }  public async SaveFlowInfo(params: FlowPayloadModel): Promise<ApiResponseModel | null> {
            try {
                const url = `Api/FlowAction/SaveFlowInfo`;
                const response = await this._provider!.post(url, params);
                const res = ApiResponseModel.fromJson(response);
                res.data = response["data"];
                return res;
            } catch (error) {
                Logger.event('API_RESPONSE_ERROR', `Failed to SaveFlowInfo: ${error}`);
                throw new Error(`Failed to SaveFlowInfo: ${error}`);
            }
        }
    
        public async DeleteFlowInfo(Id: number): Promise<ApiResponseModel | null> {
            try {
                const url = `Api/FlowAction/DeleteFlowInfo`;
                const formData = new FormData();
                formData.append('Id', Id.toString());
                const response = await this._provider!.postForm(url, formData);
                const res = ApiResponseModel.fromJson(response);
                res.data = response["data"];
                return res;
            } catch (error) {
                Logger.event('API_RESPONSE_ERROR', `Failed to DeleteFlowInfo: ${error}`);
                throw new Error(`Failed to DeleteFlowInfo: ${error}`);
            }
        }
    
    //#endregion Flow
    
   //#region Flow 
    
   public async GetAllFlowSettingByFlow(params: FlowSettingGetPayloadModel): Promise<{
    rows: FlowSettingModel[];
    pagination: PaginationModel;
    } | null> {
        try {
            const url = `Api/FlowInfo/GetAllFlowSettingByFlow`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get FlowSettingByFlow list');
                return null;
            }

            return {
                rows: response["data"]["rows"].map((js: any) => FlowSettingModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get FlowSettingByFlow list: ${error}`);
            throw new Error(`Failed to get FlowSettingByFlow list: ${error}`);
        }
    }
    public async GetFlowSetting(params: FlowSettingGetPayloadModel): Promise<FlowSettingModel| null> {
        try {
            const url = `Api/FlowInfo/GetFlowSetting`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get FlowSetting info');
                return null;
            }
            return FlowSettingModel.fromJson(response["data"]);

        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
                throw new Error(`Api failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
                throw new Error('Api failed: Unknown error');
            }
        }
    }  public async SaveFlowSettingInfo(params: FlowSettingPayloadModel): Promise<ApiResponseModel | null> {
            try {
                const url = `Api/FlowAction/SaveFlowSettingInfo`;
                const response = await this._provider!.post(url, params);
                const res = ApiResponseModel.fromJson(response);
                res.data = response["data"];
                return res;
            } catch (error) {
                Logger.event('API_RESPONSE_ERROR', `Failed to SaveFlowInfo: ${error}`);
                throw new Error(`Failed to SaveFlowInfo: ${error}`);
            }
        }
    
        public async DeleteFlowSettingInfo(Id: number): Promise<ApiResponseModel | null> {
            try {
                const url = `Api/FlowAction/DeleteFlowSettingInfo`;
                const formData = new FormData();
                formData.append('Id', Id.toString());
                const response = await this._provider!.postForm(url, formData);
                const res = ApiResponseModel.fromJson(response);
                res.data = response["data"];
                return res;
            } catch (error) {
                Logger.event('API_RESPONSE_ERROR', `Failed to DeleteFlowInfo: ${error}`);
                throw new Error(`Failed to DeleteFlowInfo: ${error}`);
            }
        }
    
    //#endregion FlowSetting
   
   //#region FlowSettingResult   
    
   public async GetAllFlowSettingResultBySetting(params: FlowSettingResultGetPayloadModel): Promise<{
    rows: FlowSettingResultModel[];
    pagination: PaginationModel;
    } | null> {
        try {
            const url = `Api/FlowInfo/GetAllFlowSettingResultBySetting`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get FlowSettingByFlow list');
                return null;
            }

            return {
                rows: response["data"]["rows"].map((js: any) => FlowSettingResultModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get FlowSettingByFlow list: ${error}`);
            throw new Error(`Failed to get FlowSettingByFlow list: ${error}`);
        }
    }
    public async GetFlowSettingResult(params: FlowSettingResultGetPayloadModel): Promise<FlowSettingResultModel| null> {
        try {
            const url = `Api/FlowInfo/GetFlowSettingResult`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get FlowSettingResult info');
                return null;
            }
            return FlowSettingResultModel.fromJson(response["data"]);

        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
                throw new Error(`Api failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
                throw new Error('Api failed: Unknown error');
            }
        }
    }  public async SaveFlowSettingResultInfo(params: FlowSettingResultPayloadModel): Promise<ApiResponseModel | null> {
            try {
                const url = `Api/FlowAction/SaveFlowSettingResultInfo`;
                const response = await this._provider!.post(url, params);
                const res = ApiResponseModel.fromJson(response);
                res.data = response["data"];
                return res;
            } catch (error) {
                Logger.event('API_RESPONSE_ERROR', `Failed to SaveFlowResultInfo: ${error}`);
                throw new Error(`Failed to SaveFlowInfo: ${error}`);
            }
        }
    
        public async DeleteFlowSettingResultInfo(Id: number): Promise<ApiResponseModel | null> {
            try {
                const url = `Api/FlowAction/DeleteFlowSettingResultInfo`;
                const formData = new FormData();
                formData.append('Id', Id.toString());
                const response = await this._provider!.postForm(url, formData);
                const res = ApiResponseModel.fromJson(response);
                res.data = response["data"];
                return res;
            } catch (error) {
                Logger.event('API_RESPONSE_ERROR', `Failed to DeleteFlowResultInfo: ${error}`);
                throw new Error(`Failed to DeleteFlowInfo: ${error}`);
            }
        }
    
    //#endregion FlowSetting Result
    
   //#region FlowFormBuilderDesign
    
   public async GetAllFlowFormBuilderDesignByFlow(params: FlowFormBuilderDesignGetPayloadModel): Promise<{
    rows: FlowFormBuilderDesignModel[];
    pagination: PaginationModel;
    } | null> {
        try {
            const url = `Api/FlowInfo/GetAllFlowFormBuilderDesignByFlow`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get FlowSettingByFlow list');
                return null;
            }

            return {
                rows: response["data"]["rows"].map((js: any) => FlowFormBuilderDesignModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get FlowSettingByFlow list: ${error}`);
            throw new Error(`Failed to get FlowSettingByFlow list: ${error}`);
        }
    }
    public async GetFlowFormBuilderDesign(params: FlowFormBuilderDesignGetPayloadModel): Promise<FlowFormBuilderDesignModel| null> {
        try {
            const url = `Api/FlowInfo/GetFlowFormBuilderDesign`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get FlowFormBuilderDesign info');
                return null;
            }
            return FlowFormBuilderDesignModel.fromJson(response["data"]);

        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
                throw new Error(`Api failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
                throw new Error('Api failed: Unknown error');
            }
        }
    }  public async SaveFlowFormBuilderDesignInfo(params: FlowFormBuilderDesignPayloadListModel): Promise<ApiResponseModel | null> {
            try {
                const url = `Api/FlowAction/SaveFlowFormBuilderDesignInfo`;
                const response = await this._provider!.post(url, params);
                const res = ApiResponseModel.fromJson(response);
                res.data = response["data"];
                return res;
            } catch (error) {
                Logger.event('API_RESPONSE_ERROR', `Failed to SaveFlowResultInfo: ${error}`);
                throw new Error(`Failed to SaveFlowInfo: ${error}`);
            }
        }
    
        public async DeleteFlowFormBuilderDesignInfo(Id: number): Promise<ApiResponseModel | null> {
            try {
                const url = `Api/FlowAction/DeleteFlowFormBuilderDesignInfo`;
                const formData = new FormData();
                formData.append('Id', Id.toString());
                const response = await this._provider!.postForm(url, formData);
                const res = ApiResponseModel.fromJson(response);
                res.data = response["data"];
                return res;
            } catch (error) {
                Logger.event('API_RESPONSE_ERROR', `Failed to DeleteFlowResultInfo: ${error}`);
                throw new Error(`Failed to DeleteFlowInfo: ${error}`);
            }
        }
    
    //#endregion FlowFormBuilderDesign
   
   
}

export default FlowRepository;
